import { z } from 'zod'
import { sanitizeText, sanitizeEmail } from './sanitization'
import { getApiUrl } from './apiClient'

/**
 * Custom Zod validation utilities for ProSci Portal
 */

// Removed unused emailUniquenessValidator - functionality moved to commonSchemas.uniqueEmail

/**
 * Password validator - checks if password is correct
 * Requires user ID for authentication context
 */
const passwordValidator = (userId?: string) => z.string().refine(
  async (password) => {
    if (!password) return true // Skip validation for empty passwords

    if (!userId) {
      throw new Error('User ID is required for password validation')
    }

    try {
      const response = await fetch(getApiUrl('/api/users/validate_password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',  // Include cookies for authentication
        body: JSON.stringify({ password, user_id: userId }),
      })

      // Check for HTTP errors (including auth failures)
      if (!response.ok) {
        return false
      }

      const data = await response.json()
      // Check for both error formats: { error: '...' } and { errors: [...] }
      return !data.error && !data.errors
    } catch {
      throw new Error('Something went wrong')
    }
  },
  {
    message: 'Password was incorrect'
  }
)

/**
 * Common validation schemas
 */
export const commonSchemas = {
  // String with length limits (sanitized)
  requiredString: (maxLength: number = 255) =>
    z.string()
      .transform(sanitizeText)
      .refine(val => val.length > 0, { message: 'This field is required' })
      .refine(val => val.length <= maxLength, { message: `Must be ${maxLength} characters or less` }),

  // Optional string with length limits (sanitized)
  optionalString: (maxLength: number = 255) =>
    z.string()
      .transform(sanitizeText)
      .refine(val => val.length <= maxLength, { message: `Must be ${maxLength} characters or less` })
      .optional(),

  // Email validation (sanitized)
  email: z.string()
    .transform(sanitizeEmail)
    .refine(val => val.length > 0, { message: 'Email is required' })
    .refine(val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'Please enter a valid email address' }),

  // Email validation with custom max length
  emailWithLength: (maxLength: number = 255) =>
    z.string()
      .transform(sanitizeEmail)
      .refine(val => val.length > 0, { message: 'Email is required' })
      .refine(val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'Please enter a valid email address' })
      .refine(val => val.length <= maxLength, { message: `Must be ${maxLength} characters or less` }),

  // Email with uniqueness check (sanitized)
  uniqueEmail: z.string()
    .transform(sanitizeEmail)
    .refine(val => val.length > 0, { message: 'Email is required' })
    .refine(val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'Please enter a valid email address' })
    .refine(
      async (email) => {
        if (!email) return true
        try {
          const response = await fetch(getApiUrl(`/api/users/find/email?email=${encodeURIComponent(email)}&lookup_email_only=true`), {
            credentials: 'include',  // Include cookies for authentication
          })

          // Check for HTTP errors (including auth failures)
          if (!response.ok) {
            throw new Error('Unable to verify email availability')
          }

          const data = await response.json()
          // If the response is empty object {}, email is unique (available)
          // If the response contains user data, email is taken
          return Object.keys(data).length === 0
        } catch {
          throw new Error('Something went wrong')
        }
      },
      { message: 'Email is already taken' }
    ),

  // Password validation - requires user ID
  currentPassword: (userId?: string) => passwordValidator(userId),

  // Boolean checkbox validation
  requiredCheckbox: z.boolean()
    .refine(val => val === true, {
      message: 'You must agree to the terms and conditions before proceeding'
    }),

  // Select/enum validation
  requiredSelect: z.string()
    .min(1, { message: 'Please make a selection' }),
}

/**
 * Email confirmation validator
 */
export function createEmailConfirmationSchema(emailField: string = 'email') {
  return z.string()
    .transform(sanitizeEmail)
    .refine(val => val.length > 0, { message: 'Email confirmation is required' })
    .refine(val => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), { message: 'Please enter a valid email address' })
    .refine((value, ctx) => {
      // Safety check for context and root
      if (!ctx || !ctx.root) {
        return true // Skip validation if no root context
      }
      
      const formData = ctx.root as Record<string, unknown>
      let email: unknown
      
      if (emailField.includes('.')) {
        const keys = emailField.split('.')
        email = keys.reduce((obj: unknown, key: string) => {
          return obj && typeof obj === 'object' && key in obj 
            ? (obj as Record<string, unknown>)[key] 
            : undefined
        }, formData)
      } else {
        email = formData[emailField]
      }
      
      return value === email
    }, {
      message: 'Emails must match'
    })
}

/**
 * Utility to convert Zod errors to Formik-compatible error format
 */
export function zodToFormikErrors(zodError: z.ZodError): Record<string, unknown> {
  const errors: Record<string, unknown> = {}
  
  for (const issue of zodError.issues) {
    const path = issue.path.join('.')
    
    if (path.includes('.')) {
      // Handle nested paths like 'user.email'
      const keys = path.split('.')
      let current = errors
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
          current[keys[i]] = {}
        }
        current = current[keys[i]] as Record<string, unknown>
      }
      
      current[keys[keys.length - 1]] = issue.message
    } else {
      errors[path] = issue.message
    }
  }
  
  return errors
}

/**
 * Synchronous validation function compatible with Formik
 */
export function createSyncValidator<T>(schema: z.ZodSchema<T>) {
  return (values: unknown) => {
    try {
      schema.parse(values)
      return {}
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formikErrors = zodToFormikErrors(error)
        return formikErrors
      }
      return { general: `Validation failed: ${error.message}` }
    }
  }
}

/**
 * Asynchronous validation function compatible with Formik
 */
export function createAsyncValidator<T>(schema: z.ZodSchema<T>) {
  return async (values: unknown) => {
    try {
      await schema.parseAsync(values)
      return {}
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formikErrors = zodToFormikErrors(error)
        return formikErrors
      }
      return { general: `Validation failed: ${error.message}` }
    }
  }
}